import { useState, useEffect } from "react";
// The sidebar surface is dark in both light and dark app themes, so it always
// uses the dark-teal icon rather than the light-surface icon mark.
import iconMarkAsset from "@/assets/enviroeng_icon_dark_teal.png.asset.json";


import {
  LayoutDashboard,
  FileText,
  MapPin,
  Map as MapIcon,
  Radio,
  Building2,
  User,
  Settings,
  Shield,
  Leaf,
  LogOut,
  Bell,
  Inbox,
  Brain,
  FolderLock,
  Upload,
  Send,
  ShieldCheck,
  BellRing,
  BarChart3,
  Activity,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";

const mainNav = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Inbox", url: "/inbox", icon: Inbox },
  { title: "Reminders", url: "/reminders", icon: BellRing },
  { title: "Escalation Policies", url: "/escalation-policies", icon: ShieldCheck },
  { title: "Reports", url: "/reports", icon: FileText },
  { title: "Sites", url: "/sites", icon: MapPin },
  { title: "Field Data", url: "/field", icon: Radio },
  { title: "Field Readings", url: "/field-readings", icon: Activity },
  { title: "GIS Map", url: "/gis", icon: MapIcon },
  { title: "Alert History", url: "/notifications", icon: Bell },
  { title: "Delivery Log", url: "/delivery-log", icon: Send },
  { title: "Notification Audit", url: "/notification-audit", icon: Shield },
];

const aiNav = [
  { title: "AI Insights", url: "/insights", icon: Brain, badge: "New" },
  { title: "Document Vault", url: "/documents", icon: FolderLock, showDocCount: true },
  { title: "Compliance & Audit", url: "/compliance-audit", icon: ShieldCheck },
  { title: "Search Performance", url: "/seo/performance", icon: BarChart3 },
  { title: "SEO Operations", url: "/seo/ops", icon: ShieldCheck },
  { title: "Sign-up Tracking", url: "/admin/signups", icon: ShieldCheck },
  { title: "SEO Preview", url: "/seo/preview", icon: FileText },
];

const orgNav = [
  { title: "Company", url: "/company", icon: Building2 },
  { title: "Profile & Career", url: "/profile", icon: User },
  { title: "Settings", url: "/settings", icon: Settings },
];
// Slim pill nav item. `NavLink` here is the local compat wrapper, which takes
// plain strings for base/active classes rather than a render function.
const NAV_LINK_BASE =
  "press relative flex items-center gap-2.5 py-2 px-2.5 rounded-lg transition-all text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar-background";
const NAV_LINK_ACTIVE =
  "!bg-primary/15 !text-sidebar-foreground font-medium before:absolute before:left-0 before:top-1/2 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-primary";




export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string) => location.pathname.startsWith(path);
  const { user } = useAuth();
  const [docCount, setDocCount] = useState(0);
  const [unanalyzedCount, setUnanalyzedCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const loadCounts = async () => {
      const [total, unanalyzed] = await Promise.all([
        supabase.from("documents").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("documents").select("*", { count: "exact", head: true }).eq("user_id", user.id).eq("status", "uploaded"),
      ]);
      setDocCount(total.count ?? 0);
      setUnanalyzedCount(unanalyzed.count ?? 0);
    };
    loadCounts();
  }, [user]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sidebar-accent/40 p-1">
            <img src={iconMarkAsset.url} alt="EnviroEng Pro" width={128} height={128} className="h-full w-auto object-contain" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-[15px] font-bold tracking-tight text-sidebar-foreground">
                EnviroEng Pro
              </span>
              <span className="text-[10px] text-sidebar-foreground/75 font-medium">
                Environmental Intelligence
              </span>
            </div>
          )}
        </div>

      </SidebarHeader>

      <SidebarSeparator className="opacity-30 mx-3" />

      <SidebarContent className="px-2" data-tour="sidebar">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-[0.15em] text-sidebar-foreground/75 font-semibold px-2 mb-1" id="nav-group-operations">Operations</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu aria-labelledby="nav-group-operations">
              {mainNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink
                      to={item.url}
                      className={NAV_LINK_BASE}
                      activeClassName={NAV_LINK_ACTIVE}
                      aria-label={item.title}
                      title={collapsed ? item.title : undefined}

                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                      {!collapsed && <span className="text-[13px]">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="opacity-30 mx-3" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-[0.15em] text-sidebar-foreground/75 font-semibold px-2 mb-1" id="nav-group-intelligence">Intelligence</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu aria-labelledby="nav-group-intelligence">
              {aiNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink
                      to={item.url}
                      className={NAV_LINK_BASE}
                      activeClassName={NAV_LINK_ACTIVE}
                      aria-label={item.title}
                      title={collapsed ? item.title : undefined}

                    >
                      <div className="relative">
                        <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                        {item.showDocCount && unanalyzedCount > 0 && (
                          <span
                            className="absolute -top-1 -right-1.5 h-2.5 w-2.5 rounded-full bg-warning border-2 border-sidebar-background animate-pulse"
                            role="status"
                            aria-label={`${unanalyzedCount} documents awaiting analysis`}
                          />
                        )}
                      </div>
                      {!collapsed && (
                        <span className="flex items-center gap-2 text-[13px]">
                          {item.title}
                          {item.badge && (
                            <Badge className="text-[9px] px-1.5 py-0 h-4 bg-primary text-primary-foreground border-0 font-semibold">
                              <span className="sr-only">Status: </span>
                              {item.badge}
                            </Badge>
                          )}
                          {item.showDocCount && docCount > 0 && (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-sidebar-border/80 text-sidebar-foreground/75">
                              {docCount}
                              <span className="sr-only"> documents</span>
                            </Badge>
                          )}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="opacity-30 mx-3" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-[0.15em] text-sidebar-foreground/75 font-semibold px-2 mb-1" id="nav-group-organization">Organization</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu aria-labelledby="nav-group-organization">
              {orgNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink
                      to={item.url}
                      className={NAV_LINK_BASE}
                      activeClassName={NAV_LINK_ACTIVE}
                      aria-label={item.title}
                      title={collapsed ? item.title : undefined}

                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                      {!collapsed && <span className="text-[13px]">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3 space-y-2">
        {!collapsed && (
          <Button
            variant="outline"
            size="sm"
            className="press w-full justify-start gap-2 text-xs font-medium border-sidebar-border/60 bg-primary/10 text-sidebar-foreground hover:bg-primary/20 hover:text-sidebar-foreground transition-colors"
            onClick={() => navigate("/documents")}
            aria-label="Quick upload a document"
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Quick Upload
          </Button>
        )}
        <LogoutButton collapsed={collapsed} />
        {!collapsed && (
          <div className="flex items-center gap-2 rounded-lg bg-sidebar-accent/40 p-2.5 border border-sidebar-border/40">
            <Shield className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] text-sidebar-foreground/75 font-medium">
              SOC 2 · HIPAA Ready
            </span>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

function LogoutButton({ collapsed }: { collapsed: boolean }) {
  const { signOut, user } = useAuth();
  return (
    <Button variant="ghost" size="sm" aria-label="Sign out" title={collapsed ? "Sign out" : undefined} className="press w-full justify-start gap-2 text-sidebar-foreground/85 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors" onClick={signOut}>
      <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="text-xs truncate">{user?.email || "Sign out"}</span>}
    </Button>
  );
}
